import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { TimelineChart } from "@/components/timeline/TimelineChart";
import { useWorkHub } from "@/components/work/useWorkHub";
import { FollowUpDialog, TaskDialog } from "@/components/work/TaskDialog";
import { CompleteDialog } from "@/components/work/CompleteDialog";
import type { TimelineGrouping } from "@/lib/timeline";

export const Route = createFileRoute("/_authenticated/work/timeline")({
  head: () => ({
    meta: [
      { title: "Timeline — MyPriorities" },
      {
        name: "description",
        content:
          "A live Gantt of your tasks and meetings, with QC, publishing sign-off and due dates marked on each bar.",
      },
      { property: "og:title", content: "Timeline — MyPriorities" },
      {
        property: "og:description",
        content: "See how your delivery dates and internal deadlines line up week by week.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TimelinePage,
});

function TimelinePage() {
  const hub = useWorkHub();
  const [grouping, setGrouping] = useState<TimelineGrouping>("none");

  const names = {
    pillars: Object.fromEntries(hub.plan.pillars.map((p) => [p.id, p.name])),
    goals: Object.fromEntries(hub.plan.goals.map((g) => [g.id, g.name])),
  };

  const teamCount = hub.openTasks.filter((t) => t.is_team_task).length;

  return (
    <AppShell
      title="Timeline"
      subtitle={`${hub.openTasks.length} open items — ${teamCount} run as team tasks.`}
      action={
        <Button variant="outline" asChild>
          <Link to="/work">
            <ArrowLeft className="size-4" /> Work hub
          </Link>
        </Button>
      }
    >
      <div className="mt-4">
        <TimelineChart
          tasks={hub.openTasks}
          grouping={grouping}
          onGroupingChange={setGrouping}
          names={names}
          onSelect={(id) => {
            const task = hub.tasks.find((t) => t.id === id);
            if (task) hub.openEdit(task);
          }}
        />
      </div>

      <TaskDialog hub={hub} />
      <CompleteDialog hub={hub} />
      <FollowUpDialog hub={hub} />
    </AppShell>
  );
}
